import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  AlertTriangle, 
  RefreshCw, 
  MessageSquare, 
  Brain, 
  CheckCircle,
  XCircle,
  Lightbulb,
  ArrowRight
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ExtractionError {
  type: 'network' | 'parsing' | 'ai_provider' | 'validation' | 'timeout';
  message: string;
  code?: string;
  context?: Record<string, any>;
  suggestedFixes?: string[];
  retryable: boolean;
}

interface ExtractionErrorRecoveryProps {
  error: ExtractionError | null;
  onRetry: () => Promise<void>;
  onManualFallback: () => void;
  onReportIssue: (feedback: string) => Promise<void>;
  isRetrying: boolean;
  className?: string;
}

export function ExtractionErrorRecovery({
  error,
  onRetry,
  onManualFallback,
  onReportIssue,
  isRetrying,
  className
}: ExtractionErrorRecoveryProps) {
  const [feedback, setFeedback] = useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const { toast } = useToast();

  if (!error) return null;

  const getErrorIcon = (type: string) => {
    switch (type) {
      case 'network':
        return <XCircle className="h-5 w-5 text-destructive" />;
      case 'ai_provider':
        return <Brain className="h-5 w-5 text-destructive" />;
      case 'timeout':
        return <AlertTriangle className="h-5 w-5 text-warning" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-destructive" />;
    }
  };

  const getErrorSeverity = (type: string) => {
    if (type === 'timeout' || type === 'network') return 'warning';
    return 'destructive';
  };

  const handleSubmitFeedback = async () => {
    if (!feedback.trim()) {
      toast({
        title: "Feedback Required",
        description: "Please provide feedback about the issue",
        variant: "destructive",
      });
      return;
    }

    setIsSubmittingFeedback(true);
    try {
      await onReportIssue(feedback);
      setFeedback('');
      toast({
        title: "Feedback Submitted",
        description: "Thank you for helping us improve the system",
      });
    } catch (err) {
      toast({
        title: "Failed to Submit",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {getErrorIcon(error.type)}
          Extraction Error
          <Badge variant={getErrorSeverity(error.type) as any}>
            {error.type.replace('_', ' ').toUpperCase()}
          </Badge>
        </CardTitle>
      </CardHeader>
      
      <CardContent>
        <Tabs defaultValue="details" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="solutions">Solutions</TabsTrigger>
            <TabsTrigger value="feedback">Feedback</TabsTrigger>
          </TabsList>
          
          <TabsContent value="details" className="space-y-4">
            <Alert variant={getErrorSeverity(error.type) as any}>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                {error.message}
              </AlertDescription>
            </Alert>

            {error.code && (
              <div className="text-xs text-muted-foreground font-mono">
                Error Code: {error.code}
              </div>
            )}

            {error.context && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Context Information</h4>
                <div className="text-xs bg-muted p-3 rounded-md font-mono">
                  {JSON.stringify(error.context, null, 2)}
                </div>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="solutions" className="space-y-4">
            <div className="space-y-3">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <Lightbulb className="h-4 w-4" />
                Recovery Options
              </h4>
              
              {error.retryable && (
                <div className="p-3 border rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm">Automatic Retry</div>
                      <div className="text-xs text-muted-foreground">
                        Retry the extraction with the same parameters
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      onClick={onRetry}
                      disabled={isRetrying}
                    >
                      {isRetrying ? (
                        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      )}
                      Retry
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="p-3 border rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">Manual Extraction</div>
                    <div className="text-xs text-muted-foreground">
                      Fall back to manual field entry
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={onManualFallback}
                  >
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Switch to Manual
                  </Button>
                </div>
              </div>

              {error.suggestedFixes && error.suggestedFixes.length > 0 && (
                <div className="space-y-2">
                  <h5 className="text-sm font-medium">Suggested Fixes</h5>
                  <ul className="space-y-1">
                    {error.suggestedFixes.map((fix, index) => (
                      <li key={index} className="text-xs text-muted-foreground flex items-start gap-2">
                        <CheckCircle className="h-3 w-3 mt-0.5 text-success flex-shrink-0" />
                        {fix}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="feedback" className="space-y-4">
            <div className="space-y-3">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Help Us Improve
              </h4>
              
              <div className="text-xs text-muted-foreground">
                Your feedback helps us improve the AI extraction system. Please describe what you were trying to extract or any patterns you noticed.
              </div>
              
              <Textarea
                placeholder="Describe the issue or what you expected to happen..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={4}
              />
              
              <Button 
                onClick={handleSubmitFeedback}
                disabled={isSubmittingFeedback || !feedback.trim()}
                className="w-full"
              >
                {isSubmittingFeedback ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <MessageSquare className="h-4 w-4 mr-2" />
                )}
                Submit Feedback
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}